import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { api, type Sesja, type Zdrowie } from '../api';
import { APP_INFO } from '../app-info';
import { idz } from '../router';
import {
  IkonaKonto,
  IkonaKontrahenci,
  IkonaMontaze,
  IkonaSzukaj,
  IkonaUrzadzenia,
  IkonaUstawienia,
  IkonaWyloguj,
  IkonaZlecenia,
} from './Icons';

/**
 * Szkielet ekranu: ciemna nawigacja po lewej, pasek u góry, przewijana treść.
 * Układ z prototypu (docs/prototyp-desktop.html).
 *
 * Nawigacja pionowa, a nie zakładki u góry, bo pozycji będzie przybywać z każdym
 * etapem — zlecenia, planowanie, montaże, kosztorysy. Poniżej 1024 px zwija się do
 * samych ikon zamiast znikać: serwisant na telefonie też musi umieć przejść do listy.
 */

interface PozycjaNawigacji {
  sciezka: string;
  nazwa: string;
  ikona: ReactNode;
  uprawnienie: string | null;
  licznik?: number;
}

/**
 * Tytuł i akcje należą do widoku, ale wyświetlają się w pasku powłoki. Zamiast
 * przenosić je stanem przez pół drzewa (i ryzykować pętlę przy każdym renderze),
 * widok wstawia je portalem w gniazda przygotowane przez powłokę.
 */
const GniazdaContext = createContext<{ tytul: HTMLElement | null; akcje: HTMLElement | null }>({
  tytul: null,
  akcje: null,
});

export function NaglowekWidoku({
  tytul,
  podtytul,
  akcje,
}: {
  tytul: string;
  podtytul?: string;
  akcje?: ReactNode;
}) {
  const gniazda = useContext(GniazdaContext);

  useEffect(() => {
    document.title = `${tytul} — Klindro`;
  }, [tytul]);

  return (
    <>
      {gniazda.tytul &&
        createPortal(
          <div className="min-w-0">
            <h1 className="t-h2 truncate">{tytul}</h1>
            {podtytul && <div className="t-small truncate text-n-600">{podtytul}</div>}
          </div>,
          gniazda.tytul,
        )}
      {gniazda.akcje && akcje && createPortal(akcje, gniazda.akcje)}
    </>
  );
}

/** Kafel znaku. Jedyny promień w aplikacji (CLAUDE.md §5). */
function ZnakKlindro({ rozmiar = 30 }: { rozmiar?: number }) {
  return (
    <span
      aria-hidden="true"
      className="flex shrink-0 items-center justify-center"
      style={{
        width: rozmiar,
        height: rozmiar,
        borderRadius: 'var(--radius-mark)',
        background: 'var(--brand-500)',
        color: 'var(--surface)',
        font: 'var(--text-h2)',
        letterSpacing: '-0.04em',
      }}
    >
      K
    </span>
  );
}

export function Shell({
  sesja,
  sciezka,
  szukane,
  zdrowie,
  onWyloguj,
  children,
}: {
  sesja: Sesja;
  sciezka: string;
  szukane: string;
  zdrowie: Zdrowie | null;
  onWyloguj: () => void;
  children: ReactNode;
}) {
  const [gniazda, setGniazda] = useState<{ tytul: HTMLElement | null; akcje: HTMLElement | null }>({
    tytul: null,
    akcje: null,
  });
  const [liczniki, setLiczniki] = useState<{
    urzadzen?: number;
    kontrahentow?: number;
    montazy?: number;
    zlecen?: number;
  }>({});

  /*
   * Uchwyty do gniazd paska muszą mieć STAŁĄ tożsamość. Funkcja `ref` wstawiona
   * wprost w JSX jest przy każdym renderze nowa, więc React odpina starą (wywołuje
   * ją z `null`) i przypina nową — a skoro obie ustawiają stan, render wywołuje
   * render bez końca i strona zostaje biała.
   */
  const gniazdoTytulu = useCallback((el: HTMLElement | null) => {
    setGniazda((poprzednie) =>
      poprzednie.tytul === el ? poprzednie : { ...poprzednie, tytul: el },
    );
  }, []);

  const gniazdoAkcji = useCallback((el: HTMLElement | null) => {
    setGniazda((poprzednie) =>
      poprzednie.akcje === el ? poprzednie : { ...poprzednie, akcje: el },
    );
  }, []);

  const mozeUrzadzenia = sesja.permissions.includes('device.read');
  const mozeKontrahenci = sesja.permissions.includes('customer.read');
  const mozeMontaze = sesja.permissions.includes('assembly.read');
  const mozeZlecenia = sesja.permissions.includes('ticket.read');

  useEffect(() => {
    if (mozeUrzadzenia) {
      api
        .urzadzenia({ limit: 1 })
        .then((w) => setLiczniki((p) => ({ ...p, urzadzen: w.ile })))
        .catch(() => undefined);
    }
    if (mozeKontrahenci) {
      api
        .kontrahenci({ limit: 1 })
        .then((w) => setLiczniki((p) => ({ ...p, kontrahentow: w.ile })))
        .catch(() => undefined);
    }
    if (mozeMontaze) {
      // Licznik pokazuje montaże NIEZAKOŃCZONE — to one wymagają czyjejś reakcji.
      Promise.all([
        api.montaze({ status: 'NOWE', limit: 1 }),
        api.montaze({ status: 'W_TOKU', limit: 1 }),
      ])
        .then(([nowe, wToku]) =>
          setLiczniki((p) => ({ ...p, montazy: nowe.ile + wToku.ile })),
        )
        .catch(() => undefined);
    }
    if (mozeZlecenia) {
      // Licznik pokazuje zlecenia BEZ PRZYPISANIA — to jedyny wskaźnik, który
      // wymaga reakcji dziś. Reszta jest informacyjna.
      api
        .zlecenia({ fakt: 'bez-przypisania', limit: 1 })
        .then((w) => setLiczniki((p) => ({ ...p, zlecen: w.ile })))
        .catch(() => undefined);
    }
  }, [mozeUrzadzenia, mozeKontrahenci, mozeMontaze, mozeZlecenia, sciezka]);

  const pozycje: PozycjaNawigacji[] = [
    {
      sciezka: '/zlecenia',
      nazwa: 'Zlecenia',
      ikona: <IkonaZlecenia />,
      uprawnienie: 'ticket.read',
      licznik: liczniki.zlecen,
    },
    {
      sciezka: '/urzadzenia',
      nazwa: 'Urządzenia',
      ikona: <IkonaUrzadzenia />,
      uprawnienie: 'device.read',
      licznik: liczniki.urzadzen,
    },
    {
      sciezka: '/montaze',
      nazwa: 'Montaże',
      ikona: <IkonaMontaze />,
      uprawnienie: 'assembly.read',
      licznik: liczniki.montazy,
    },
    {
      sciezka: '/kontrahenci',
      nazwa: 'Kontrahenci',
      ikona: <IkonaKontrahenci />,
      uprawnienie: 'customer.read',
      licznik: liczniki.kontrahentow,
    },
    {
      sciezka: '/ustawienia',
      nazwa: 'Ustawienia',
      ikona: <IkonaUstawienia />,
      uprawnienie: 'settings.read',
    },
    { sciezka: '/konto', nazwa: 'Konto', ikona: <IkonaKonto />, uprawnienie: null },
  ].filter((p) => p.uprawnienie === null || sesja.permissions.includes(p.uprawnienie));

  const inicjaly = sesja.user.imieNazwisko
    .split(/\s+/)
    .slice(0, 2)
    .map((czesc) => czesc[0] ?? '')
    .join('')
    .toUpperCase();

  return (
    <div className="flex h-screen">
      {/*
        Nawigacja boczna wyłącznie na szerokim ekranie. Na telefonie ta sama nawigacja
        wraca paskiem u dołu: pionowa szyna ikon zjadała 58 px z 390, a i tak trafiało
        się w nią kciukiem najgorzej ze wszystkich miejsc na ekranie.
      */}
      <nav
        className="hidden w-[var(--nav-w)] shrink-0 flex-col gap-4 overflow-hidden bg-n-900 px-13 py-14 lg:flex"
        aria-label="Nawigacja główna"
      >
        <div className="mb-13 flex items-center gap-10 px-4">
          {/* Kafel znaku — jedyny promień w aplikacji i jedyne miejsce, gdzie kolor
              marki jest tożsamością, a nie akcją ani wyborem (CLAUDE.md §5). */}
          <ZnakKlindro />
          <div className="min-w-0 flex-1">
            <div className="t-body-strong truncate text-surface">Klindro</div>
            <div className="t-small truncate text-n-500">{sesja.user.tenant.nazwa}</div>
          </div>
        </div>

        {pozycje.map((pozycja) => {
          const aktywna = sciezka.startsWith(pozycja.sciezka);
          return (
            <button
              key={pozycja.sciezka}
              type="button"
              onClick={() => idz(pozycja.sciezka)}
              aria-current={aktywna ? 'page' : undefined}
              // Aktywna pozycja niesie kolor marki jako oznaczenie wyboru.
              className={`flex items-center gap-10 border-0 border-l-2 bg-transparent px-9 text-left ${
                aktywna
                  ? 'border-l-brand-500 bg-n-800 text-surface'
                  : 'border-l-transparent text-n-300 hover:bg-n-800 hover:text-surface'
              }`}
            >
              <span className="shrink-0">{pozycja.ikona}</span>
              <span className={aktywna ? 't-body-strong' : 't-body'}>{pozycja.nazwa}</span>
              {pozycja.licznik !== undefined && (
                <span className="t-mono-data ml-auto text-brand-400">{pozycja.licznik}</span>
              )}
            </button>
          );
        })}

        {/* Metadane wydania na dole nawigacji — wersja z scripts/app.ts jest jednym
            źródłem prawdy, więc rozjazd „co stoi na serwerze" widać od razu. */}
        <div className="mt-auto px-9">
          <div className="t-mono-data text-n-500">
            {APP_INFO.name} {APP_INFO.version}
          </div>
          <div className="t-small text-n-500">{APP_INFO.updatedAt}</div>
          {zdrowie && (
            <div className="t-small text-n-500">
              API {zdrowie.wersja} · baza {zdrowie.baza}
            </div>
          )}
          {zdrowie && zdrowie.wersja !== APP_INFO.version && (
            <div className="t-small mt-5 text-[var(--status-progress)]">
              Wersja interfejsu i API są różne — odśwież stronę.
            </div>
          )}
          {/* Środowisko inne niż produkcyjne mówi o sobie wprost. Na maszynie
              dewelopera to oczywiste, na serwerze testowym już nie. */}
          {zdrowie?.srodowisko && zdrowie.srodowisko !== 'produkcja' && (
            <div className="t-small mt-5 text-[var(--status-progress)]">
              środowisko {zdrowie.srodowisko}
            </div>
          )}
          <div className="t-small mt-5 text-n-600">{APP_INFO.copyright}</div>
        </div>
      </nav>

      <nav
        className="fixed inset-x-0 bottom-0 z-10 flex h-[var(--nav-h-dol)] border-t border-n-800 bg-n-900 lg:hidden"
        aria-label="Nawigacja główna"
      >
        {pozycje.map((pozycja) => {
          const aktywna = sciezka.startsWith(pozycja.sciezka);
          return (
            <button
              key={pozycja.sciezka}
              type="button"
              onClick={() => idz(pozycja.sciezka)}
              aria-current={aktywna ? 'page' : undefined}
              className={`flex flex-1 flex-col items-center justify-center gap-4 border-0 border-t-2 bg-transparent px-4 ${
                aktywna ? 'border-t-brand-500 text-surface' : 'border-t-transparent text-n-300'
              }`}
            >
              <span className="relative">
                {pozycja.ikona}
                {pozycja.licznik !== undefined && pozycja.licznik > 0 && (
                  <span className="t-mono-micro absolute -top-5 -right-9 text-brand-400">
                    {pozycja.licznik}
                  </span>
                )}
              </span>
              <span className="t-small leading-none">{pozycja.nazwa}</span>
            </button>
          );
        })}
      </nav>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex min-h-[var(--top-h)] shrink-0 flex-wrap items-center gap-13 border-b border-n-100 bg-surface px-18 py-7">
          <div ref={gniazdoTytulu} className="order-1 min-w-0 flex-1 lg:order-none lg:flex-none" />

          <Szukajka sciezka={sciezka} poczatkowe={szukane} />

          {/* Akcje widoku dostają na telefonie własny wiersz — „Skanuj" i „Nowa karta"
              nie zmieszczą się obok tytułu na 390 px, a to one są tu do klikania. */}
          <div
            className="order-3 flex w-full items-center gap-9 lg:order-none lg:w-auto"
            ref={gniazdoAkcji}
          />

          <div className="order-1 flex items-center gap-9 lg:order-none">
            <span
              aria-hidden="true"
              className="t-label flex h-[30px] w-[30px] shrink-0 items-center justify-center bg-n-900 text-surface"
            >
              {inicjaly}
            </span>
            <button
              type="button"
              onClick={onWyloguj}
              title="Wyloguj"
              aria-label="Wyloguj"
              className="flex items-center border border-n-300 bg-surface px-10 text-n-700 hover:bg-n-50"
            >
              <IkonaWyloguj />
            </button>
          </div>
        </header>

        <main className="min-h-0 flex-1 overflow-y-auto px-18 py-18 pb-[calc(var(--nav-h-dol)+var(--space-18))] lg:pb-18">
          <GniazdaContext.Provider value={gniazda}>{children}</GniazdaContext.Provider>
        </main>
      </div>
    </div>
  );
}

/**
 * Wyszukiwarka w pasku filtruje listę, na której stoisz; z widoku szczegółów
 * prowadzi do listy urządzeń. Jedno pole, nie osobne pudełko na każdym ekranie.
 */
function Szukajka({ sciezka, poczatkowe }: { sciezka: string; poczatkowe: string }) {
  const [tekst, setTekst] = useState(poczatkowe);
  const pole = useRef<HTMLInputElement>(null);

  useEffect(() => setTekst(poczatkowe), [poczatkowe]);

  // Ukośnik ustawia kursor w wyszukiwarce — skrót z prototypu.
  useEffect(() => {
    const reaguj = (zdarzenie: KeyboardEvent): void => {
      if (zdarzenie.key !== '/' || zdarzenie.ctrlKey || zdarzenie.altKey) return;
      const cel = zdarzenie.target as HTMLElement | null;
      if (cel && /^(INPUT|TEXTAREA|SELECT)$/.test(cel.tagName)) return;
      zdarzenie.preventDefault();
      pole.current?.focus();
    };
    window.addEventListener('keydown', reaguj);
    return () => window.removeEventListener('keydown', reaguj);
  }, []);

  const docelowa = sciezka.startsWith('/kontrahenci')
    ? '/kontrahenci'
    : sciezka.startsWith('/montaze')
      ? '/montaze'
      : sciezka.startsWith('/zlecenia')
        ? '/zlecenia'
        : '/urzadzenia';

  return (
    <form
      onSubmit={(zdarzenie) => {
        zdarzenie.preventDefault();
        const fraza = tekst.trim();
        idz(fraza ? `${docelowa}?q=${encodeURIComponent(fraza)}` : docelowa);
      }}
      className="relative order-2 w-full min-w-[180px] lg:order-none lg:mr-auto lg:w-auto lg:flex-1 lg:max-w-[420px]"
      role="search"
    >
      <span className="pointer-events-none absolute top-1/2 left-10 -translate-y-1/2 text-n-500">
        <IkonaSzukaj rozmiar={16} />
      </span>
      <input
        ref={pole}
        value={tekst}
        onChange={(zdarzenie) => setTekst(zdarzenie.target.value)}
        placeholder="Szukaj: klient, nr boczny, nr fabryczny, model…"
        aria-label="Szukaj"
        autoComplete="off"
        className="w-full border border-n-300 bg-n-50 py-7 pr-34 pl-34 text-n-900"
      />
      <span className="t-mono-data pointer-events-none absolute top-1/2 right-10 -translate-y-1/2 text-n-500">
        /
      </span>
    </form>
  );
}
